import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronLeft } from 'lucide-react';
import { NavLink } from './header/NavLink';
import { NavDropdown } from './header/NavDropdown';
import { MobileNavLink } from './header/MobileNavLink';
import SafeChurchLogo from './SafeChurchLogo';

const SafeChurchCorner = ({ testId, size }) => (
  <Link to="/ministries/restore/safe-church" aria-label="Safe Church compliant ministry — learn more" title="Safe Church Compliant" className="inline-flex shrink-0 rounded-full transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" data-testid={testId}>
    <SafeChurchLogo size={size} variant="dark" />
  </Link>
);

const ABOUT_ITEMS = [
  { to: '/about', label: 'About Us' },
  { to: '/team', label: 'Meet the Team' },
  { to: '/beliefs', label: 'Our Beliefs' },
];

const FASTING_LINK = { to: '/ministries/prayer-fasting', label: 'Prayer & Fasting' };

const CONTENT_ITEMS = [
  { to: '/watch-live', label: 'Watch Live' },
  { to: '/sermons', label: 'Sermons' },
  { to: '/pastor-corner', label: "Pastor's Corner" },
  { to: '/devotional', label: 'Weekly Devotional' },
  { href: 'https://www.youtube.com/@LifeChurchofNJ', label: 'YouTube Channel', external: true },
];

const MOBILE_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  { to: '/team', label: 'Meet the Team' },
  { to: '/beliefs', label: 'Our Beliefs' },
  { to: '/ministries', label: 'Ministries' },
  { to: '/what-to-expect', label: 'Visit' },
  { to: '/watch-live', label: 'Watch Live' },
  { to: '/sermons', label: 'Sermons' },
  { to: '/pastor-corner', label: "Pastor's Corner" },
  { to: '/devotional', label: 'Weekly Devotional' },
];

// Prayer & Fasting only appears in the menu while the Super Admin has it visible.
const useFastingVisible = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/site-pages/prayer-fasting`).then(() => setVisible(true)).catch(() => setVisible(false));
  }, []);
  return visible;
};

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';
  const fastingVisible = useFastingVisible();
  const contentItems = fastingVisible ? [...CONTENT_ITEMS.slice(0, 4), FASTING_LINK, CONTENT_ITEMS[4]] : CONTENT_ITEMS;
  const mobileLinks = fastingVisible ? [...MOBILE_LINKS, FASTING_LINK] : MOBILE_LINKS;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 pwa-safe-header">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between lg:grid lg:grid-cols-3">
          {/* Mobile Back Button - shows on non-home pages, spacer on home */}
          <div className="lg:hidden flex items-center gap-1 w-[76px]">
          {!isHome && (
            <button
              className="lg:hidden text-slate-600 p-1.5 -ml-1 rounded-lg hover:bg-green-50 active:bg-green-100 transition-colors"
              onClick={() => navigate(-1)}
              data-testid="mobile-back-btn"
              aria-label="Go back"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
            <SafeChurchCorner testId="header-safe-church-logo-mobile" size={32} />
          </div>

          {/* Left Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <SafeChurchCorner testId="header-safe-church-logo" size={40} />
            <NavLink to="/" isActive={isActive('/')}>Home</NavLink>
            <NavDropdown
              label="About"
              isActive={isActive('/about') || isActive('/team') || isActive('/beliefs')}
              items={ABOUT_ITEMS}
            />
            <NavLink to="/ministries" isActive={isActive('/ministries')}>Ministries</NavLink>
          </nav>

          {/* Centered Logo */}
          <Link to="/" className="flex items-center justify-center">
            <img src="/logo-new.png" alt="Life Church of New Jersey logo" className="h-10 sm:h-14" loading="eager" />
          </Link>

          {/* Right Navigation */}
          <nav className="hidden lg:flex items-center gap-1 justify-end">
            <NavLink to="/what-to-expect" isActive={isActive('/what-to-expect')}>Visit</NavLink>
            <NavDropdown
              label="Content"
              isActive={['/watch-live', '/sermons', '/pastor-corner', '/devotional', FASTING_LINK.to].some(isActive)}
              items={contentItems}
              alignRight
            />
            <Link to="/connect">
              <Button className="bg-church-green hover:bg-church-green-dark font-semibold ml-2">
                Connect
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-700 p-2 w-[76px] flex justify-end"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-200 pt-4 bg-white">
            <div className="flex flex-col space-y-1">
              {mobileLinks.map((l) => (
                <MobileNavLink
                  key={l.to}
                  to={l.to}
                  isActive={isActive(l.to)}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {l.label}
                </MobileNavLink>
              ))}
              <MobileNavLink
                to="/connect"
                primary
                onClick={() => setMobileMenuOpen(false)}
              >
                Connect
              </MobileNavLink>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
